import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Shield, TrendingUp, Users, FileText, Building2 } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const assetOverview = [
  "Codified authority systems",
  "Operator certification and licensing framework",
  "Governance, review, and compliance processes",
  "Brand-controlled deployment assets",
];

const notAcquired = [
  "Founder personality or personal brand dependency",
  "Unstructured consulting revenue",
  "Key-person operational risk",
];

const investmentHighlights = [
  "Proprietary authority operating system",
  "Founder-independent demand control",
  "Operator-transferable execution model",
  "Licensing and certification expansion potential",
  "High-margin, low-capex infrastructure business",
];

const transitionTimeline = [
  {
    days: "Day 0–30",
    title: "Authority Separation",
    details: [
      "Founder exits client-facing delivery",
      "Operator Certification governs execution",
      "Internal Review Dashboard assumes approvals",
    ],
  },
  {
    days: "Day 31–60",
    title: "Governance-Only Role",
    details: [
      "Founder retains veto rights via Authority Council",
      "No operational involvement",
      "Brand and IP enforcement only",
    ],
  },
  {
    days: "Day 61–90",
    title: "Buyer-Grade Independence",
    details: [
      "Founder role reduced to IP steward",
      "Operators fully autonomous",
      "System demonstrates independence under observation",
    ],
  },
];

const BuyerAccessPage = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect to admin if not authenticated (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/admin");
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Show loading while redirect is happening
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="buyer-access-page">
      <SEOHead 
        title="Buyer Overview - Confidential"
        description="Confidential buyer overview for OnPoint Authority Systems infrastructure."
        url="https://onpointauthority.com/buyer-access"
      />
      <Navbar />

      {/* Confidential Banner */}
      <div className="bg-primary/10 border-b border-primary/20 py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <Lock className="h-4 w-4 text-primary" />
          <p className="text-xs text-primary font-medium tracking-wider uppercase">
            Confidential — Authorized Access Only
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-6 md:px-12" data-testid="buyer-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Enterprise Authority Infrastructure
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Buyer<br />
              <span className="text-primary">Overview</span>
            </h1>
            <p className="text-lg font-light leading-relaxed text-muted-foreground max-w-2xl">
              This page is provided to qualified buyers, strategic partners, and investors under NDA.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is Being Acquired */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="asset-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-4">
                The Asset
              </p>
              <h2 className="font-serif text-4xl font-normal tracking-tight text-foreground mb-6">
                What Buyers Are<br />
                <span className="text-primary">Acquiring</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                A proprietary authority infrastructure platform consisting of:
              </p>
              <ul className="space-y-3">
                {assetOverview.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground">
                    <Shield className="h-4 w-4 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-red-500/5 border border-red-500/20 p-8"
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-4">
                What Is NOT Being Acquired
              </p>
              <ul className="space-y-3">
                {notAcquired.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-muted-foreground">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Investment Highlights */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Executive Summary
            </p>
            <h2 className="font-serif text-4xl font-normal tracking-tight text-foreground">
              Investment<br />
              <span className="text-primary">Highlights</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {investmentHighlights.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 border border-white/5 bg-card/5"
              >
                <TrendingUp className="h-4 w-4 text-primary flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-foreground">{item}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 p-6 border border-primary/20 bg-primary/5"
          >
            <p className="text-sm text-foreground">
              <span className="text-primary font-medium">Why This Commands a Premium:</span>{" "}
              The system produces repeatable, licensable authority outcomes independent of the founder, 
              enabling clean EBITDA attribution and scalable growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Transition */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="transition-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Risk Mitigation
            </p>
            <h2 className="font-serif text-4xl font-normal tracking-tight text-foreground">
              90-Day Founder<br />
              <span className="text-primary">Substitution Map</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {transitionTimeline.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-6"
              >
                <p className="text-xs font-mono text-primary mb-2">{phase.days}</p>
                <h3 className="font-serif text-xl text-foreground mb-4">{phase.title}</h3>
                <ul className="space-y-2">
                  {phase.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <div className="w-1 h-1 bg-primary rounded-full mt-2" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="next-steps-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Transaction Path
            </p>
            <h2 className="font-serif text-4xl font-normal tracking-tight text-foreground mb-8">
              Next<br />
              <span className="text-primary">Steps</span>
            </h2>

            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {["NDA", "CIM Review", "Management Discussion", "Indication of Interest"].map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-primary">{String(index + 1).padStart(2, '0')}</span>
                  <span className="text-sm text-foreground">{step}</span>
                  {index < 3 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mb-8">
              This microsite is not marketed. It is provided selectively to qualified buyers under NDA.
            </p>

            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="buyer-cta"
              >
                Request Confidential Access
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BuyerAccessPage;
